import { ref, computed, triggerRef } from 'vue';
import type { Document } from '@/entities/Document';

const selectedDocumentIds = ref<Set<number>>(new Set());

export default function useDocumentSelection() {
  const isDocumentSelected = (id: number) => {
    return selectedDocumentIds.value.has(id);
  };

  const toggleDocumentSelection = (id: number) => {
    if (selectedDocumentIds.value.has(id)) {
      selectedDocumentIds.value.delete(id);
    } else {
      selectedDocumentIds.value.add(id);
    }
    triggerRef(selectedDocumentIds);
  };

  const selectAllDocuments = (documents: Document[]) => {
    documents.forEach((doc) => selectedDocumentIds.value.add(doc.id));
    triggerRef(selectedDocumentIds);
  };

  const deselectAllDocuments = () => {
    selectedDocumentIds.value.clear();
    triggerRef(selectedDocumentIds);
  };

  const toggleAllDocuments = (documents: Document[]) => {
    const allSelected = documents.every((doc) => selectedDocumentIds.value.has(doc.id));
    if (allSelected) {
      deselectAllDocuments();
    } else {
      selectAllDocuments(documents);
    }
  };

  const selectedCount = computed(() => selectedDocumentIds.value.size);

  const selectedDocuments = computed(() => Array.from(selectedDocumentIds.value));

  const hasSelectedDocuments = computed(() => selectedDocumentIds.value.size > 0);

  const resetSelection = () => {
    selectedDocumentIds.value.clear();
    triggerRef(selectedDocumentIds);
  };

  return {
    selectedCount,
    selectedDocuments,
    hasSelectedDocuments,
    isDocumentSelected,
    toggleDocumentSelection,
    selectAllDocuments,
    deselectAllDocuments,
    toggleAllDocuments,
    resetSelection,
  };
}
